import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faDownload,
  faSpinner,
  faPlus,
  faMinus,
  faRedo,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const ChatImageLightbox = ({ imageUrl, onClose }) => {
  const { config: { isDark } = {} } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState(true);
  const [zoomScale, setZoomScale] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Cerrar con Escape
  useEffect(() => {
    if (!imageUrl) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageUrl, onClose]);

  // Reset al cambiar imageUrl
  useEffect(() => {
    setZoomScale(1);
    setPanPosition({ x: 0, y: 0 });
    setIsDragging(false);
    setIsLoading(true);
  }, [imageUrl]);

  const handleZoomIn = (e) => {
    e?.stopPropagation();
    setZoomScale(prev => Math.min(Number((prev + 0.5).toFixed(1)), 4));
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation();
    setZoomScale(prev => {
      const next = Math.max(Number((prev - 0.5).toFixed(1)), 1);
      if (next === 1) setPanPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = (e) => {
    e?.stopPropagation();
    setZoomScale(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleWheelZoom = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoomScale(prev => Math.min(Number((prev + 0.25).toFixed(2)), 4));
    } else {
      setZoomScale(prev => {
        const next = Math.max(Number((prev - 0.25).toFixed(2)), 1);
        if (next === 1) setPanPosition({ x: 0, y: 0 });
        return next;
      });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomScale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoomScale <= 1) return;
    e.preventDefault();
    setPanPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  if (!imageUrl) return null;

  return (
    <div
      className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-between p-3"
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        zIndex: 40,
        backdropFilter: 'blur(6px)'
      }}
      onClick={onClose}
    >
      {/* Barra superior con botón volver y cerrar */}
      <div
        className="d-flex align-items-center justify-content-between w-100 flex-shrink-0 mb-2"
        onClick={e => e.stopPropagation()}
        style={{ zIndex: 5 }}
      >
        <button
          type="button"
          onClick={onClose}
          className="btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill shadow-sm"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            fontSize: '0.82rem',
            fontWeight: 500
          }}
          title="Volver a la conversación (Esc)"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Volver</span>
        </button>
      </div>

      {/* Contenedor central de la imagen con soporte de zoom y arrastre */}
      <div
        className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100 overflow-hidden rounded-3"
        style={{ minHeight: 0, userSelect: 'none' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Spinner de carga */}
        {isLoading && (
          <div className="position-absolute d-flex flex-column align-items-center justify-content-center py-5">
            <FontAwesomeIcon icon={faSpinner} spin className="text-white mb-2" style={{ fontSize: '2rem' }} />
            <span className="text-white-50" style={{ fontSize: '0.85rem' }}>Cargando imagen...</span>
          </div>
        )}

        <div
          className="d-flex align-items-center justify-content-center w-100 h-100 overflow-hidden"
          style={{
            cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onWheel={handleWheelZoom}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={() => setZoomScale(prev => (prev > 1 ? 1 : 2))}
        >
          <img
            src={imageUrl}
            alt="Vista ampliada"
            className="img-fluid rounded-2 shadow-lg"
            draggable={false}
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              margin: 'auto',
              objectFit: 'contain',
              display: isLoading ? 'none' : 'block',
              transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomScale})`,
              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
              transformOrigin: 'center center'
            }}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        </div>
      </div>

      {/* Barra de herramientas inferior con Zoom, Descargar y Cerrar */}
      <div
        className="d-flex flex-column align-items-center w-100 flex-shrink-0 mt-2"
        onClick={e => e.stopPropagation()}
        style={{ zIndex: 5 }}
      >
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 rounded-pill shadow-lg"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            gap: '14px'
          }}
        >
          {/* Controles de Zoom */}
          <div className="d-flex align-items-center gap-1.5">
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center justify-content-center p-0 shadow-none"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: zoomScale <= 1 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.20)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: zoomScale <= 1 ? 'rgba(255, 255, 255, 0.35)' : '#ffffff',
                cursor: zoomScale <= 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease'
              }}
              onClick={handleZoomOut}
              disabled={zoomScale <= 1}
              title="Alejar (-)"
            >
              <FontAwesomeIcon icon={faMinus} style={{ fontSize: '0.85rem' }} />
            </button>

            <span
              className="px-2 fw-bold text-center user-select-none"
              style={{
                fontSize: '0.82rem',
                minWidth: '50px',
                color: '#ffffff',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              {Math.round(zoomScale * 100)}%
            </span>

            <button
              type="button"
              className="btn btn-sm d-flex align-items-center justify-content-center p-0 shadow-none"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: zoomScale >= 4 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.20)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: zoomScale >= 4 ? 'rgba(255, 255, 255, 0.35)' : '#ffffff',
                cursor: zoomScale >= 4 ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease'
              }}
              onClick={handleZoomIn}
              disabled={zoomScale >= 4}
              title="Acercar (+)"
            >
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.85rem' }} />
            </button>

            {zoomScale > 1 && (
              <button
                type="button"
                className="btn btn-sm rounded-pill px-2.5 py-0 ms-1 d-flex align-items-center gap-1 shadow-none"
                style={{
                  fontSize: '0.74rem',
                  height: '28px',
                  backgroundColor: 'rgba(56, 189, 248, 0.2)',
                  border: '1px solid #38bdf8',
                  color: '#38bdf8',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                onClick={handleResetZoom}
                title="Restablecer tamaño original"
              >
                <FontAwesomeIcon icon={faRedo} style={{ fontSize: '0.65rem' }} />
                Reset
              </button>
            )}
          </div>

          <div style={{ width: '1px', height: '22px', backgroundColor: 'rgba(255, 255, 255, 0.25)' }} />

          {/* Acciones de Descarga y Cierre */}
          <div className="d-flex align-items-center gap-2">
            <a
              href={imageUrl}
              download="imagen_whatsapp.jpg"
              className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                border: 'none',
                textDecoration: 'none'
              }}
              title="Descargar imagen"
            >
              <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.85rem' }} />
            </a>
            <button
              type="button"
              className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 shadow-sm"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none'
              }}
              onClick={onClose}
              title="Cerrar (Esc)"
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.9rem' }} />
            </button>
          </div>
        </div>

        {/* Ayuda visual cuando hay zoom */}
        {zoomScale > 1 && (
          <span className="text-white-50 mt-1" style={{ fontSize: '0.75rem' }}>
            💡 Arrastra con el ratón para moverte por la imagen · Doble clic para alternar zoom
          </span>
        )}
      </div>
    </div>
  );
};

ChatImageLightbox.propTypes = {
  imageUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export default ChatImageLightbox;
